import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal, Overline } from "./Reveal";
import { PORTFOLIO_ITEMS } from "../data/content";

const FILTER_KEYS = ["murano", "uomo", "feel", "gliori", "vaccari", "bags", "mela"];
const FILTER_TESTIDS = {
  murano: "portfolio-category-filter-murano",
  uomo: "portfolio-category-filter-uomo",
  feel: "portfolio-category-filter-feel",
  gliori: "portfolio-category-filter-gliori",
  vaccari: "portfolio-category-filter-vaccari",
  bags: "portfolio-category-filter-bags",
  mela: "portfolio-category-filter-mela",
};

const Portfolio = () => {
  const { lang, t } = useLanguage();
  const [filter, setFilter] = useState("murano");
  const [zoomIdx, setZoomIdx] = useState(null);

  const items = PORTFOLIO_ITEMS.filter((i) => i.category === filter);
  const zoom = zoomIdx !== null ? items[zoomIdx] : null;

  const closeZoom = useCallback(() => {
    setZoomIdx(null);
    window.__lenis?.start();
  }, []);
  const prevZoom = useCallback(() => {
    setZoomIdx((i) => (i - 1 + items.length) % items.length);
  }, [items.length]);
  const nextZoom = useCallback(() => {
    setZoomIdx((i) => (i + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (zoomIdx === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeZoom();
      if (e.key === "ArrowLeft") prevZoom();
      if (e.key === "ArrowRight") nextZoom();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomIdx, closeZoom, prevZoom, nextZoom]);

  const openZoom = (idx) => {
    setZoomIdx(idx);
    window.__lenis?.stop();
  };

  return (
    <section id="progetti" data-testid="portfolio-section" className="py-24 sm:py-36 bg-travertine overflow-hidden">
      <div className="max-w-[92rem] mx-auto px-4 sm:px-6 lg:px-10">
        <Reveal>
          <Overline>{t.portfolio.overline}</Overline>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mt-4">
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">
              {t.portfolio.title}
            </h2>
            <p className="text-sm text-ink/55 max-w-sm leading-relaxed">{t.portfolio.intro}</p>
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-12 flex flex-wrap gap-3">
          {FILTER_KEYS.map((key) => (
            <button
              key={key}
              data-testid={FILTER_TESTIDS[key]}
              onClick={() => setFilter(key)}
              className={`px-5 py-2.5 text-[11px] font-mono uppercase tracking-[0.18em] rounded-full border transition-all duration-400 ${
                filter === key
                  ? "bg-ink text-travertine-light border-ink"
                  : "border-ink/15 text-ink/60 hover:border-gold hover:text-gold"
              }`}
            >
              {t.portfolio.filters[key]}
            </button>
          ))}
        </Reveal>

        <motion.div layout data-testid="portfolio-grid" className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {items.map((item, i) => (
              <motion.article
                layout
                key={item.id}
                data-testid={`portfolio-item-${item.id}`}
                initial={{ opacity: 0, y: 40, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative ${item.span}`}
              >
                <div
                  data-testid={`portfolio-zoom-${item.id}`}
                  onClick={() => openZoom(i)}
                  className={`relative overflow-hidden frame-gold rounded-[28px] cursor-pointer transition-shadow duration-500 group-hover:shadow-[0_44px_80px_-28px_rgba(28,25,23,0.5)] ${item.aspect}`}
                >
                  <img
                    src={item.image}
                    alt={item.title[lang]}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/0 to-ink/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                    <h3 className="font-serif text-2xl text-travertine-light mt-1">{item.title[lang]}</h3>
                  </div>
                </div>
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-gold/80 mt-4 px-1">
                  {t.portfolio.categories[item.category]}
                </p>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {zoom !== null && (
          <motion.div
            data-testid="portfolio-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[95] flex items-center justify-center p-4 sm:p-10 bg-ink/75 backdrop-blur-sm"
            onClick={closeZoom}
          >
            <motion.figure
              key={zoom.id}
              initial={{ opacity: 0, scale: 0.9, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 16 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-[92vw] max-h-[88vh]"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={zoom.image}
                alt={zoom.title[lang]}
                className="max-w-[92vw] max-h-[80vh] w-auto h-auto object-contain rounded-[24px] frame-gold mx-auto"
              />
              <figcaption
                data-testid="portfolio-lightbox-caption"
                className="text-center text-[11px] font-mono uppercase tracking-[0.25em] text-travertine-light/80 mt-4"
              >
                {zoom.title[lang]}
              </figcaption>
            </motion.figure>

            <button
              data-testid="portfolio-lightbox-prev"
              onClick={(e) => { e.stopPropagation(); prevZoom(); }}
              aria-label="Previous"
              className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-travertine-light/90 text-ink hover:bg-gold hover:text-travertine-light transition-colors duration-300"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              data-testid="portfolio-lightbox-next"
              onClick={(e) => { e.stopPropagation(); nextZoom(); }}
              aria-label="Next"
              className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-travertine-light/90 text-ink hover:bg-gold hover:text-travertine-light transition-colors duration-300"
            >
              <ChevronRight size={20} />
            </button>
            <button
              data-testid="portfolio-lightbox-close"
              onClick={closeZoom}
              aria-label="Close"
              className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-travertine-light/90 text-ink hover:bg-gold hover:text-travertine-light transition-colors duration-300"
            >
              <X size={18} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default Portfolio;
